import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Box, LinearProgress, Alert, Autocomplete, TextField } from "@mui/material";
import "leaflet/dist/leaflet.css";
import { MapContainer, GeoJSON, TileLayer, useMap } from "react-leaflet";
import { FitBounds } from "./FitBounds";
import { SEGMENT_COLORS } from "../../Constants";

const height = "500px";
const loadingMessage = "Loading map with road segment(s). Thank you for your patience.";
const segmentColor = SEGMENT_COLORS.texas100;

const tileLayer = (
    <TileLayer
        url={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
        tileSize={512}
        zoomOffset={-1}
        attribution='&copy; <a href="https://www.mapbox.com/about/maps/" tabindex="-1">Mapbox</a>'
    />
);

// Hides the Leaflet container from screen readers. The <section> wrapper provides the accessible landmark.
function AriaHideMap() {
    const map = useMap();
    useEffect(() => {
        map.getContainer().setAttribute("aria-hidden", "true");
    }, [map]);
    return null;
}

//Highlights every layer whose feature id is in highlightedIds and fits the map to the union of
//their bounds. Opens a popup when a NEW id was just added to the highlighted set
function ZoomAndHighlight({ layerRefs, highlightedIds, lastHighlightedId, allFeatureIds }) {
    const map = useMap();
    const prevHighlightedRef = useRef(null);

    useEffect(() => {
        const prevIds = prevHighlightedRef.current;
        const isFirstRun = prevIds === null;
        const newlyAddedIds = highlightedIds.filter((id) => !(prevIds || []).includes(id));
        const removedIds = (prevIds || []).filter((id) => !highlightedIds.includes(id));

        //Deselection: restore that layer's original (pre-highlight) styling and close its popup if open.
        removedIds.forEach((id) => {
            const layer = layerRefs.current[id];
            if (!layer) return;
            if (layer.isPopupOpen?.()) layer.closePopup();
            if (layer._baseStyle) {
                layer.setStyle({ weight: 5, color: layer._baseStyle.color, dashArray: layer._baseStyle.dashArray, fillOpacity: 0.7 });
            }
        });

        const transitionedToEmpty = !isFirstRun && removedIds.length > 0 && highlightedIds.length === 0;
        prevHighlightedRef.current = highlightedIds;

        //Nothing highlighted (and we just came from having highlights) - return to the default
        //view, fit to every available segment
        if (transitionedToEmpty) {
            const allLayers = (allFeatureIds || [])
                .map((id) => layerRefs.current[id])
                .filter(Boolean);

            let defaultBounds = null;
            allLayers.forEach((layer) => {
                try {
                    const b = layer.getBounds();
                    defaultBounds = defaultBounds ? defaultBounds.extend(b) : b;
                } catch {
                    // layer may not expose bounds (e.g. point feature) skip it for bounds purposes
                }
            });

            if (defaultBounds) {
                try {
                    map.fitBounds(defaultBounds, { maxZoom: 14 });
                } catch {
                    // ignore - leave map at its current view if fitBounds fails
                }
            }
            return;
        }

        if (!highlightedIds || highlightedIds.length === 0) return;

        const layers = highlightedIds
            .map((id) => layerRefs.current[id])
            .filter(Boolean);

        if (layers.length === 0) return;

        //Style all highlighted layers consistently.
        layers.forEach((layer) => {
            layer.setStyle({ weight: 10, color: layer.options.color, opacity: 1, dashArray: "" });
        });

        //Combine bounds across all highlighted layers.
        let combinedBounds = null;
        layers.forEach((layer) => {
            try {
                const b = layer.getBounds();
                combinedBounds = combinedBounds ? combinedBounds.extend(b) : b;
            } catch {
                // layer may not expose bounds (e.g. point feature) -skip it for bounds purposes
            }
        });

        //Only open a popup if this update is the result of a NEW selection
        const isNewSelection = newlyAddedIds.length > 0;
        const targetLayer = layerRefs.current[lastHighlightedId];

        const openTargetPopup = () => {
            if (!isNewSelection || !targetLayer) return;
            if (targetLayer.isPopupOpen?.()) targetLayer.closePopup();
            targetLayer.openPopup?.();
        };

        if (combinedBounds) {
            try {
                map.fitBounds(combinedBounds, {
                    paddingTopLeft: [50, 220],
                    paddingBottomRight: [50, 50],
                    maxZoom: 14,
                });
                if (map.isMoving()) {
                    map.once("moveend", () => requestAnimationFrame(openTargetPopup));
                } else {
                    openTargetPopup();
                }
            } catch {
                openTargetPopup();
            }
        } else {
            openTargetPopup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedIds, lastHighlightedId]);

    return null;
}

/**
 * Reusable roadways map component.
 *
 * Props:
 *   data             {Array}    GeoJSON feature array
 *   isLoading        {boolean}  Whether data is still loading
 *   onEachFeature    {Function} (feature, layer) — page-specific popup/click setup
 *   ariaLabel        {string}   aria-label for the map element
 *   mapStyle         {Object}   Extra inline styles merged into MapContainer style
 *   highlightedIds   {Array}    Feature ids to highlight, fit bounds to, and (for the most recent) open popup for
 *   lastHighlightedId {*}       Most-recently-toggled-on id within highlightedIds; its popup is opened
 *   layerRefs        {Ref}      Ref object populated by onEachFeature; required when highlightedIds is used
 *   onFeatureSelect  {Function} (featureId) => void handler invoked when a feature is selected via the autocomplete dropdown
 *   showAutocomplete {boolean} Whether to show the autocomplete dropdown for feature selection
 *   interactive      {boolean} Whether to enable hover and popup interactions on the GeoJSON layer (default: true)
 *   styleFunction    {Function} Optional function to determine GeoJSON layer styles, overriding default styling
 *   geoJSONKey       {string} Optional key to force GeoJSON layer re-render when data updates but reference doesn't change
 *   children         {ReactNode} Optional additional children to render inside MapContainer (e.g. additional layers or controls)
 */
export default function Texas100RoadSegmentsMap({
    data,
    isLoading,
    onEachFeature,
    ariaLabel = "Texas Top 100 Most Congested Roadways Map",
    mapStyle,
    highlightedIds,
    lastHighlightedId,
    layerRefs,
    onFeatureSelect,
    showAutocomplete = true,
    interactive = true,
    styleFunction,
    geoJSONKey,
    children,
}) {
    const geoJSONStyle = useCallback(() => ({ color: segmentColor, weight: 5, interactive }), [interactive]);
    const [autocompleteValue, setAutocompleteValue] = useState(null);

    const normalizedHighlightedIds = useMemo(
        () => highlightedIds ?? [],
        [highlightedIds]
    );

    const allFeatureIds = useMemo(
        () => (data || []).map((feature) => feature.id),
        [data]
    );

    const roadwayOptions = useMemo(() =>
        (data || []).map((feature) => ({
            label: `${feature.properties.road} from ${feature.properties.from_road} to ${feature.properties.to_road}`,
            feature,
        })).toSorted((a, b) => a.label.localeCompare(b.label)),
        [data]);

    //sync autocomplete selection with the most-recently-highlighted feature
    useEffect(() => {
        const targetId = lastHighlightedId ?? normalizedHighlightedIds[normalizedHighlightedIds.length - 1];
        if (!targetId) {
            setAutocompleteValue(null);
            return;
        }
        const match = roadwayOptions.find((opt) => opt.feature.id === targetId);
        setAutocompleteValue(match || null);
    }, [lastHighlightedId, normalizedHighlightedIds, roadwayOptions]);

    //wraps the provided onEachFeature with additional handlers for hover and popup styling
    function onEachFeatureWrapper(feature, layer) {
        layer._baseStyle = {
            color: layer.options.color,
            dashArray: layer.options.dashArray ?? "",
        };

        if (interactive) {
            const { color: originalColor, dashArray: originalDashArray } = layer._baseStyle;
            layer.on({
                mouseover: () => layer.setStyle({ weight: 10, color: originalColor, opacity: 1, dashArray: "" }),
                mouseout: () => {
                    const isHighlighted = normalizedHighlightedIds.includes(feature.id);
                    if (!layer.isPopupOpen() && !isHighlighted) {
                        layer.setStyle({ weight: 5, color: originalColor, dashArray: originalDashArray, fillOpacity: 0.7 });
                    }
                },
                popupopen: () => layer.setStyle({ weight: 10, color: originalColor, opacity: 1, dashArray: "" }),
                popupclose: () => {
                    const isHighlighted = normalizedHighlightedIds.includes(feature.id);
                    if (!isHighlighted) {
                        layer.setStyle({ weight: 5, color: originalColor, dashArray: originalDashArray, fillOpacity: 0.7 });
                    }
                },
            });
        }
        if (onEachFeature) onEachFeature(feature, layer);
    }

    //handler for when a feature is selected from the autocomplete dropdown
    const handleAutocompleteChange = useCallback((event, newValue) => {
        setAutocompleteValue(newValue);
        if (newValue) {
            onFeatureSelect?.(newValue.feature.id);
        } else {
            onFeatureSelect?.(null);
        }
    }, [onFeatureSelect]);

    return (
        <div style={mapStyle}>
            {!isLoading && showAutocomplete && (
                <Autocomplete
                    options={roadwayOptions}
                    value={autocompleteValue}
                    onChange={handleAutocompleteChange}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    componentsProps={{ popper: { disablePortal: true } }}
                    slotProps={{
                        popupIndicator: {
                            'aria-hidden': true,
                            'tabIndex': -1,
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search roadway by name, from, and to"
                            size="small"
                            inputProps={{
                                ...params.inputProps,
                                "aria-label": "Search roadway by name, from, and to",
                            }}
                        />
                    )}
                    sx={{ mb: 1, mt: 2 }}
                />
            )}
            {isLoading ? (
                <div>
                    <Alert role="status" severity="info" sx={{ marginBottom: "8px" }}>
                        {loadingMessage}
                    </Alert>
                    <Box sx={{ width: "100%" }} role="status" aria-busy aria-live="polite">
                        <LinearProgress aria-label={loadingMessage} />
                        <section aria-label="Map loading with road segments">
                            <MapContainer
                                style={{ height }}
                                center={[31.9686, -99.9018]}
                                minZoom={6}
                                zoom={6}
                            >
                                {tileLayer}
                                <AriaHideMap />
                            </MapContainer>
                        </section>
                    </Box>
                </div>
            ) : (
                <section aria-label={ariaLabel}>
                    <MapContainer
                        style={{ height }}
                        minZoom={6}
                        center={[31.9686, -99.9018]}
                    >
                        {tileLayer}
                        <AriaHideMap />
                        <GeoJSON
                            key={geoJSONKey ?? JSON.stringify(data)}
                            data={data}
                            onEachFeature={onEachFeatureWrapper}
                            style={styleFunction ?? geoJSONStyle}
                        />
                        <FitBounds data={data} />
                        {layerRefs && (
                            <ZoomAndHighlight
                                layerRefs={layerRefs}
                                highlightedIds={normalizedHighlightedIds}
                                lastHighlightedId={lastHighlightedId}
                                allFeatureIds={allFeatureIds}
                            />
                        )}
                        {children}
                    </MapContainer>
                </section>
            )}
        </div>
    );
}

